import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getBill, updateBill, pushBillToXero, dismissBill,
  getXeroAccounts, getXeroStatus, getBillAttachment,
  verifyBillLine, verifyBillAllLines, getSuppliers,
  reattachBillToXero, replaceBillReceipt,
  getCategories, getStores, getLocations, getWorkOrders,
  getDraftParts, updateDraftPart, approveDraftPart,
} from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel,
  SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../components/ui/command';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  ArrowLeft, ExternalLink, Save, Send, Trash2, Plus, Boxes,
  AlertTriangle, Mail, FileText, Sparkles, Eye, Download, FileWarning,
  ShieldCheck, ShieldAlert, ShieldX, Loader2, Camera, Percent, ChevronsUpDown,
  Upload, Wrench, Package, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtMoney = (v, ccy = 'GBP') => {
  if (v === null || v === undefined || v === '') return '—';
  try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: ccy }).format(v); }
  catch { return `£${Number(v || 0).toFixed(2)}`; }
};

const isInventoryAccount = (acct) => (acct?.type || '').toUpperCase() === 'DIRECTCOSTS';

const BillDetail = () => {
  const { billId } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [xeroConnected, setXeroConnected] = useState(false);
  const [accountsError, setAccountsError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Original-invoice preview state
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [pdfIsImage, setPdfIsImage] = useState(false);
  const [pdfMobileOpen, setPdfMobileOpen] = useState(false);  // mobile dialog
  const [showPreviewDesktop, setShowPreviewDesktop] = useState(true);  // desktop split-pane

  // Per-line "AI verify" state — keyed by line index
  const [verifying, setVerifying] = useState({});  // { [idx]: true }
  const [verifyingAll, setVerifyingAll] = useState(false);

  // Standard supplier discount lookup. Once the bill loads we pull the
  // supplier directory once and match by case-insensitive name. If the
  // matched supplier has a saved `defaultDiscountRate > 0` AND the parsed
  // bill is missing the discount, the UI shows a one-tap "Apply X%" banner
  // — closes the loop where the AI parser misses faded "5% trade" lines.
  // We also keep the full list around so the Supplier field can offer
  // autocomplete suggestions as the admin types.
  const [matchedSupplier, setMatchedSupplier] = useState(null);
  const [suppliers, setSuppliers] = useState([]);

  // Re-upload state — used when the original receipt is permanently lost
  // and the admin wants to attach a fresh photo to the existing Xero bill.
  const replaceInputRef = useRef(null);
  const [replacing, setReplacing] = useState(false);

  // Inventory-destination picker — chosen per COGS line BEFORE the bill
  // is pushed to Xero. Each entry captures whether the part will be
  // stocked or attached straight to a WO, plus the extra metadata that
  // path needs (store/location, or work order + already-billed flag).
  const [categories, setCategories] = useState([]);
  const [stores, setStores] = useState([]);
  const [locationsByStore, setLocationsByStore] = useState({});
  const [workOrdersList, setWorkOrdersList] = useState([]);
  // Which line index (if any) is currently editing its destination.
  const [destLineIdx, setDestLineIdx] = useState(null);
  // Draft-status parts that this bill created on push, keyed by their
  // source line description. Only populated once the bill is pushed —
  // gives the admin a way to set the destination retroactively without
  // navigating to the Draft Inventory page.
  const [draftPartsByLine, setDraftPartsByLine] = useState({});

  useEffect(() => {
    getSuppliers()
      .then((list) => setSuppliers(list || []))
      .catch(() => setSuppliers([]));
  }, []);

  useEffect(() => {
    if (!bill?.supplierName || suppliers.length === 0) {
      setMatchedSupplier(null);
      return;
    }
    const target = (bill.supplierName || '').trim().toLowerCase();
    const m = suppliers.find(s => (s.name || '').trim().toLowerCase() === target);
    setMatchedSupplier(m || null);
  }, [bill?.supplierName, suppliers]);

  useEffect(() => {
    Promise.all([
      getBill(billId).catch(() => null),
      getXeroStatus().catch(() => ({ connected: false })),
    ]).then(([b, xs]) => {
      setBill(b);
      setXeroConnected(!!xs?.connected);
    }).finally(() => setLoading(false));
  }, [billId]);

  // Preload the destination-picker data. Errors are swallowed — the
  // bill approval flow still works, just without pre-filled dropdowns.
  useEffect(() => {
    getCategories().then(setCategories).catch(() => setCategories([]));
    getStores().then(async (s) => {
      setStores(s || []);
      const map = {};
      await Promise.all((s || []).map(async st => {
        try { map[st.id] = await getLocations(st.id); }
        catch { map[st.id] = []; }
      }));
      setLocationsByStore(map);
    }).catch(() => setStores([]));
    getWorkOrders().then(setWorkOrdersList).catch(() => setWorkOrdersList([]));
  }, []);

  // Once the bill is pushed, pull the DRAFT-status parts it created so
  // the admin can retroactively set a destination (stock / attach-to-WO)
  // straight from this page — without navigating to Draft Inventory.
  // Refresh whenever the bill id / status changes (e.g. right after
  // push, or after an approval succeeds).
  const refreshBillDraftParts = async () => {
    try {
      const all = await getDraftParts();
      const mine = (all || []).filter(p => p.sourceBillId === billId);
      const byLine = {};
      mine.forEach(p => { byLine[(p.sourceLineDescription || '').trim()] = p; });
      setDraftPartsByLine(byLine);
    } catch { /* ignore — badge just won't render */ }
  };
  useEffect(() => {
    if (!bill?.id) return;
    if (bill.status !== 'pushed') { setDraftPartsByLine({}); return; }
    refreshBillDraftParts();
  }, [bill?.id, bill?.status]);

  useEffect(() => {
    if (xeroConnected) {
      getXeroAccounts()
        .then((acc) => {
          // Defensive filter: Radix UI's <SelectItem value=""> crashes the
          // entire page (blank screen) if any account has an empty code.
          const safe = (acc || []).filter(a => a && (a.code || '').trim());
          setAccounts(safe);
          setAccountsError('');
        })
        .catch((e) => {
          setAccounts([]);
          setAccountsError(e?.response?.data?.detail || 'Failed to load Xero accounts');
        });
    }
  }, [xeroConnected]);

  // Lazy-load the original PDF attachment from Gmail. We don't pre-fetch on
  // bill insert because admins often dismiss bills without reviewing.
  useEffect(() => {
    if (!bill?.id) return;
    if (!(bill.attachmentNames || []).length) return;
    // Pick the first PDF, fall back to first attachment
    const firstPdf = (bill.attachmentNames || []).find(n => /\.pdf$/i.test(n))
                     || bill.attachmentNames[0];
    setPdfLoading(true);
    setPdfError('');
    getBillAttachment(bill.id, firstPdf)
      .then((blob) => {
        // Receipts are images (JPEG/PNG/HEIC), email bills are PDFs. Trust the
        // server's Content-Type if present; otherwise default to PDF.
        const isImage = (blob.type || '').startsWith('image/')
                        || /\.(jpe?g|png|webp|heic|heif)$/i.test(firstPdf);
        const finalBlob = blob.type
          ? blob
          : new Blob([blob], { type: isImage ? 'image/jpeg' : 'application/pdf' });
        const url = URL.createObjectURL(finalBlob);
        setPdfUrl(url);
        setPdfIsImage(isImage);
      })
      .catch((e) => setPdfError(
        e?.response?.data?.detail
        || 'Could not load the original — please re-upload the file.',
      ))
      .finally(() => setPdfLoading(false));
  }, [bill?.id, bill?.attachmentNames]);

  // Free the blob URL when we leave the page or change bill.
  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  // Auto-open the file picker when we land on the page with `?reupload=1`
  // — used by the Bills Inbox "Upload" button on rows whose source file
  // is permanently lost, so the admin gets straight to the file picker.
  useEffect(() => {
    if (!bill?.id) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('reupload') === '1' && replaceInputRef.current) {
      // Strip the query param so a refresh doesn't keep re-opening it.
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => replaceInputRef.current?.click(), 200);
    }
  }, [bill?.id]);

  const handleReplaceReceipt = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';  // allow picking the same file again later
    if (!file) return;
    setReplacing(true);
    try {
      const res = await replaceBillReceipt(billId, file);
      if (res.attachedToXero) {
        toast.success(`Uploaded ${res.filename} and attached to Xero`);
      } else if (res.attachError) {
        toast.warning(`Uploaded ${res.filename} — Xero attach failed: ${res.attachError}`);
      } else {
        toast.success(`Uploaded ${res.filename}`);
      }
      // Refresh the whole bill so the preview pane reloads with the
      // new file and the amber "attachment failed" banner disappears.
      const fresh = await getBill(billId);
      setBill(fresh);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not upload receipt');
    } finally {
      setReplacing(false);
    }
  };

  const accountByCode = useMemo(() => {
    const m = {};
    accounts.forEach(a => { m[a.code] = a; });
    return m;
  }, [accounts]);

  if (loading) return <div className="p-6 text-slate-500">Loading bill...</div>;
  if (!bill) {
    return (
      <div className="p-4 sm:p-6 lg:p-8" data-testid="bill-not-found">
        <Button variant="outline" onClick={() => navigate('/admin/bills')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Bills Inbox
        </Button>
        <div className="mt-6 bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
          <div className="font-display uppercase tracking-wide text-slate-700">Bill not found</div>
        </div>
      </div>
    );
  }

  const updateField = (field, value) => {
    setBill({ ...bill, [field]: value });
    setDirty(true);
  };

  const updateLine = (idx, patch) => {
    const lines = [...(bill.lineItems || [])];
    const prev = lines[idx] || {};
    const next = { ...prev, ...patch };
    // If the admin has JUST picked an inventory (DIRECTCOSTS / cost of
    // goods sold) account on this line, immediately open the
    // destination picker so they can decide "stock it" vs "attach to
    // WO" upfront — no more silent detour through Draft Inventory.
    // Clear any prior destination when the account is switched away
    // from an inventory type (otherwise stale metadata gets shipped
    // to Xero).
    if ('accountCode' in patch && patch.accountCode !== prev.accountCode) {
      const newAcct = accountByCode[patch.accountCode];
      const oldWasInv = isInventoryAccount(accountByCode[prev.accountCode]);
      const newIsInv = isInventoryAccount(newAcct);
      if (newIsInv && !next.inventoryDestination) {
        // Defer opening so React can settle the state change first.
        setTimeout(() => setDestLineIdx(idx), 0);
      } else if (!newIsInv && oldWasInv) {
        delete next.inventoryDestination;
      }
    }
    lines[idx] = next;
    setBill({ ...bill, lineItems: lines });
    setDirty(true);
  };

  const addLine = () => {
    const lines = [...(bill.lineItems || [])];
    lines.push({ description: '', quantity: 1, unitAmount: 0, taxRate: 0.20, accountCode: '' });
    setBill({ ...bill, lineItems: lines });
    setDirty(true);
  };

  const removeLine = (idx) => {
    const lines = [...(bill.lineItems || [])];
    lines.splice(idx, 1);
    setBill({ ...bill, lineItems: lines });
    setDirty(true);
  };

  /** Apply the supplier's saved standard trade discount.
   *
   * Two modes (controlled by supplier.discountApplyMode):
   *   - 'bill': drop the rate * (sum of line gross) into bill.discount as a
   *     single trade-discount line — matches how Toolstation / Wickes print
   *     it on the till slip.
   *   - 'line': set per-line `discountRate` on every line — Xero-native, useful
   *     when the supplier itemises the discount per line on the receipt.
   *
   * Always recomputes subtotal/tax-implications via _normalise on next save
   * (we just nudge the local state so the totals row reads right).
   */
  const applyStandardDiscount = () => {
    if (!matchedSupplier) return;
    const rate = Number(matchedSupplier.defaultDiscountRate) || 0;
    if (rate <= 0) return;
    const mode = matchedSupplier.discountApplyMode || 'bill';
    const lines = [...(bill.lineItems || [])];
    let next;
    if (mode === 'line') {
      next = {
        ...bill,
        // Per-line stamp — keep bill-level discount at 0 to avoid double-counting.
        lineItems: lines.map(li => ({ ...li, discountRate: rate })),
      };
    } else {
      // Bill-level: compute as rate * sum of line gross. If we have no line
      // items yet, fall back to rate * subtotal.
      const lineGross = lines.reduce(
        (s, li) => s + (Number(li.quantity) || 0) * (Number(li.unitAmount) || 0), 0,
      );
      const base = lineGross > 0 ? lineGross : (Number(bill.subtotal) || 0);
      next = { ...bill, discount: Number((base * rate).toFixed(2)) };
    }
    setBill(next);
    setDirty(true);
    toast.success(`Applied ${(rate * 100).toFixed(1)}% ${matchedSupplier.name} trade discount — review and save`);
  };

  const runVerifyAll = async () => {
    if (dirty) {
      toast.info('Saving changes before verifying…');
      try { await save(); } catch { return; }
    }
    setVerifyingAll(true);
    try {
      const summary = await verifyBillAllLines(billId);
      // Refresh the whole bill to pull updated per-line verification verdicts.
      const fresh = await getBill(billId);
      setBill(fresh);
      const { matched = 0, partial = 0, mismatched = 0, errors = 0, total = 0 } = summary;
      const msg = `${matched}/${total} match · ${partial} partial · ${mismatched} mismatch`
        + (errors ? ` · ${errors} skipped` : '');
      if (mismatched > 0 || partial > 0) toast.warning(msg);
      else toast.success(msg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verify-all failed');
    } finally {
      setVerifyingAll(false);
    }
  };

  const runVerify = async (idx) => {
    if (dirty) {
      // Verification needs the saved line — flush edits first.
      toast.info('Saving changes before verification…');
      try { await save(); } catch { return; }
    }
    setVerifying(v => ({ ...v, [idx]: true }));
    try {
      const li = (bill.lineItems || [])[idx] || {};
      const verdict = await verifyBillLine(billId, idx, {
        description: li.description || '',
        quantity: Number(li.quantity) || 0,
        unitAmount: Number(li.unitAmount) || 0,
        sku: li.sku || '',
      });
      // Optimistic merge: update the line with the new verification verdict.
      setBill(prev => {
        const lines = [...(prev.lineItems || [])];
        lines[idx] = { ...lines[idx], verification: verdict };
        return { ...prev, lineItems: lines };
      });
      const label = verdict.status === 'match' ? 'Verified ✓' : verdict.status === 'partial' ? 'Partial match' : 'Mismatch';
      toast[verdict.status === 'match' ? 'success' : 'warning'](`${label}: ${verdict.notes || ''}`.slice(0, 120));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(v => { const n = { ...v }; delete n[idx]; return n; });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateBill(billId, {
        supplierName: bill.supplierName,
        supplierEmail: bill.supplierEmail,
        invoiceNumber: bill.invoiceNumber,
        issueDate: bill.issueDate,
        dueDate: bill.dueDate,
        subtotal: bill.subtotal,
        discount: bill.discount,        // bill-level trade discount
        tax: bill.tax,
        total: bill.total,
        notes: bill.notes,
        lineItems: bill.lineItems,
      });
      setBill(updated);
      setDirty(false);
      toast.success('Bill saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const push = async () => {
    if (dirty) {
      toast.error('Save your changes before pushing.');
      return;
    }
    if (!xeroConnected) { toast.error('Connect Xero first'); return; }
    const missing = (bill.lineItems || []).findIndex(li => !li.accountCode);
    if (missing >= 0) {
      toast.error(`Pick a Xero account for line ${missing + 1} first.`);
      return;
    }
    const directCostsLines = (bill.lineItems || []).filter(li => isInventoryAccount(accountByCode[li.accountCode]));
    const withDest = directCostsLines.filter(li => li.inventoryDestination && li.inventoryDestination.mode);
    const withoutDest = directCostsLines.length - withDest.length;
    let confirmMsg = `Push to Xero as DRAFT supplier bill?`;
    if (directCostsLines.length) {
      const parts = [];
      if (withDest.length) parts.push(`${withDest.length} line(s) will be auto-approved with your chosen destinations`);
      if (withoutDest) parts.push(`${withoutDest} line(s) will land in Draft Inventory for later review`);
      confirmMsg = `Push to Xero?\n\n${parts.join('. ')}.`;
    }
    if (!window.confirm(confirmMsg)) return;
    setPushing(true);
    try {
      const updated = await pushBillToXero(billId);
      toast.success(`Pushed to Xero: ${updated.xeroInvoiceNumber || updated.xeroInvoiceId}`);
      setBill(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Push failed');
    } finally {
      setPushing(false);
    }
  };

  const dismiss = async () => {
    if (!window.confirm('Dismiss this bill? It will be removed from the queue.')) return;
    try {
      await dismissBill(billId);
      toast.success('Dismissed');
      navigate('/admin/bills');
    } catch (e) {
      toast.error('Failed');
    }
  };

  const lines = bill.lineItems || [];
  const inventoryLineCount = lines.filter(li => isInventoryAccount(accountByCode[li.accountCode])).length;
  const isPushed = bill.status === 'pushed';
  const hasAttachment = (bill.attachmentNames || []).length > 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4" data-testid="bill-detail-page">
      {/* Top bar: back + header + actions (always full width) */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/admin/bills')} className="-ml-2">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Bills Inbox
      </Button>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Bill</div>
          <h1 className="font-display text-2xl md:text-3xl tracking-tight text-slate-900 break-words" data-testid="bill-title">
            {bill.supplierName || 'Unknown supplier'}
          </h1>
          <div className="text-sm text-slate-600 mt-1 break-words">
            {bill.emailSubject ? <span>{bill.emailSubject}</span> : null}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasAttachment && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="lg:hidden"
                onClick={() => setPdfMobileOpen(true)}
                data-testid="view-invoice-mobile-btn"
              >
                <Eye className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">View invoice</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="hidden lg:inline-flex"
                onClick={() => setShowPreviewDesktop(v => !v)}
                data-testid="toggle-invoice-preview-btn"
              >
                <Eye className="h-4 w-4 mr-2" />
                {showPreviewDesktop ? 'Hide invoice' : 'Show invoice'}
              </Button>
            </>
          )}
          {isPushed && bill.xeroDeepLink && (
            <a href={bill.xeroDeepLink} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded border border-slate-200 hover:border-slate-400">
              <ExternalLink className="h-4 w-4" /> Open in Xero
            </a>
          )}
          {!isPushed && (
            <>
              <Button variant="outline" size="sm" onClick={dismiss} data-testid="dismiss-btn">
                <Trash2 className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Dismiss</span>
              </Button>
              <Button size="sm" onClick={save} disabled={!dirty || saving}
                variant="outline" data-testid="save-btn">
                <Save className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{saving ? 'Saving...' : 'Save changes'}</span>
                <span className="sm:hidden">{saving ? '...' : 'Save'}</span>
              </Button>
              <Button size="sm" onClick={push} disabled={pushing || !xeroConnected}
                className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="push-btn">
                <Send className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{pushing ? 'Pushing...' : 'Push to Xero'}</span>
                <span className="sm:hidden">{pushing ? '...' : 'Push'}</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Split layout: sticky PDF on left (desktop only), reviewable fields on right */}
      <div className={`grid gap-6 ${hasAttachment && showPreviewDesktop ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
        {/* Left: original invoice preview (lg+ only) */}
        {hasAttachment && showPreviewDesktop && (
          <div className="hidden lg:block" data-testid="invoice-preview-pane">
            <div className="sticky top-4 bg-white border border-slate-200 rounded-md overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-4 w-4 text-slate-500 shrink-0" />
                  <span className="text-xs font-display uppercase tracking-wide text-slate-700">Original invoice</span>
                </div>
                {pdfUrl && (
                  <a
                    href={pdfUrl}
                    download={(bill.attachmentNames || [])[0] || 'invoice.pdf'}
                    className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
                    data-testid="download-invoice-link"
                  >
                    <Download className="h-3 w-3" /> Download
                  </a>
                )}
              </div>
              <PdfViewer pdfUrl={pdfUrl} pdfLoading={pdfLoading} pdfError={pdfError} pdfIsImage={pdfIsImage} heightClass="h-[78vh]" />
            </div>
          </div>
        )}

        {/* Right: reviewable form + line items */}
        <div className="space-y-4 min-w-0">

      {/* Status banners */}
      {isPushed && (
        <div className="bg-green-50 border border-green-200 text-green-900 rounded-md px-4 py-3 text-sm flex items-center gap-2" data-testid="bill-pushed-banner">
          <FileText className="h-4 w-4" />
          Pushed to Xero as DRAFT bill <strong>{bill.xeroInvoiceNumber || ''}</strong>.
          {bill.draftPartIds?.length > 0 && (
            <> Created {bill.draftPartIds.length} draft inventory item(s) — <Link to="/admin/inventory/drafts" className="underline">review them here</Link>.</>
          )}
        </div>
      )}
      {isPushed && bill.xeroAttachment && (
        bill.xeroAttachment.uploaded ? (
          <div
            className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md px-4 py-2 text-xs flex items-center gap-2"
            data-testid="bill-attachment-banner"
            title={bill.xeroAttachment.filename}
          >
            <FileText className="h-3.5 w-3.5" />
            Original PDF <span className="font-mono">{bill.xeroAttachment.filename}</span> attached to the Xero bill.
          </div>
        ) : bill.xeroAttachment.attempted ? (
          <div
            className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-4 py-2 text-xs flex items-start gap-2"
            data-testid="bill-attachment-banner"
          >
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div>Original {bill.source === 'receipt' ? 'receipt' : 'PDF'} was NOT attached to the Xero bill{bill.xeroAttachment.stage ? ` (${bill.xeroAttachment.stage})` : ''}.</div>
              {bill.xeroAttachment.error && <div className="text-amber-700 mt-0.5">{bill.xeroAttachment.error}</div>}
              {bill.xeroAttachment.reason === 'no-pdf-in-email' && <div className="text-amber-700 mt-0.5">No PDF attachment found on the source email.</div>}
              {bill.xeroAttachment.reason === 'receipt-bytes-missing' && <div className="text-amber-700 mt-0.5">The receipt image was lost (uploaded before durable storage was wired). Re-upload the receipt to attach it.</div>}
              <Button
                size="sm"
                variant="outline"
                className="mt-2 h-7 text-xs border-amber-300 hover:bg-amber-100"
                data-testid="reattach-to-xero-btn"
                onClick={async () => {
                  try {
                    const res = await reattachBillToXero(bill.id);
                    toast.success(`Attached ${res.filename} to Xero bill`);
                    const fresh = await getBill(bill.id);
                    setBill(fresh);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Could not re-attach');
                  }
                }}
              >
                <Send className="h-3 w-3 mr-1" /> Re-attach to Xero
              </Button>
            </div>
          </div>
        ) : null
      )}

      {/* Re-upload card — shown on PUSHED bills whose source file is
          lost so it can never auto-re-attach. The admin scans / picks
          a fresh photo and we attach it to the existing Xero bill in
          one shot. Visible whenever the bill is pushed AND there's
          either no attachment at all OR the previous attachment
          attempt failed and couldn't be auto-recovered. */}
      {isPushed
        && (!bill.xeroAttachment?.uploaded)
        && (
          <div
            className="bg-white border-2 border-dashed border-amber-300 rounded-md p-4 sm:p-5"
            data-testid="reupload-receipt-card"
          >
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <Upload className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display uppercase tracking-wide text-sm text-slate-800">
                  Upload a fresh receipt photo
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  This bill is in Xero but has no source receipt attached.
                  Take a photo or pick the receipt image from your phone —
                  we'll store it on the bill and attach it to the Xero invoice
                  automatically.
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <input
                    ref={replaceInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
                    capture="environment"
                    onChange={handleReplaceReceipt}
                    className="hidden"
                    data-testid="replace-receipt-input"
                  />
                  <Button
                    size="sm"
                    onClick={() => replaceInputRef.current?.click()}
                    disabled={replacing}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="replace-receipt-btn"
                  >
                    {replacing
                      ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      : <Camera className="h-4 w-4 mr-2" />}
                    {replacing ? 'Uploading…' : 'Scan / pick receipt'}
                  </Button>
                  <span className="text-[11px] text-slate-500">
                    JPEG / PNG / HEIC / PDF · max 12 MB
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      {!xeroConnected && !isPushed && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-4 py-3 text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> Xero is not connected. <Link to="/admin/xero" className="underline ml-1">Connect Xero</Link> to push this bill.
        </div>
      )}
      {xeroConnected && accountsError && !isPushed && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-4 py-3 text-sm flex items-start gap-2" data-testid="accounts-error-banner">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <strong>Xero chart of accounts unavailable.</strong> {accountsError}
            <div className="mt-1 text-xs">
              If you connected Xero before we asked for the Accounts permission, head to <Link to="/admin/xero" className="underline">Xero Settings</Link>, click <strong>Disconnect</strong> then <strong>Connect Xero</strong> again — the new prompt now requests Chart-of-Accounts access.
            </div>
          </div>
        </div>
      )}

      {/* Source: email or receipt */}
      {bill.source === 'receipt' ? (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-xs space-y-2">
          <div className="flex items-center gap-1.5 text-amber-900 font-medium">
            <Camera className="h-3.5 w-3.5" /> Source: scanned receipt
          </div>
          <div className="text-amber-800">File: <span className="font-mono">{bill.receiptFilename || '—'}</span></div>
          {bill.receiptUploadedByName && (
            <div className="text-amber-800">
              {bill.receiptUploadedByRole === 'employee' ? 'Scanned by engineer: ' : 'Uploaded by: '}
              <strong>{bill.receiptUploadedByName}</strong>
            </div>
          )}
          {bill.hint && <div className="text-amber-800">Hint: <em>"{bill.hint}"</em></div>}
          <div className="pt-2 border-t border-amber-200/60">
            <div className="uppercase tracking-wider text-[10px] text-amber-700 mb-1.5">Paid by</div>
            <div className="flex flex-wrap gap-1.5">
              {[
                { v: 'card', l: 'Company Card' },
                { v: 'cash', l: 'Cash' },
                { v: 'director-loan', l: 'Director Loan' },
                { v: 'on-account', l: 'On Account' },
              ].map((m) => (
                <button
                  key={m.v}
                  type="button"
                  onClick={() => !isPushed && updateField('paymentMethod', m.v)}
                  disabled={isPushed}
                  className={`px-2.5 py-1 rounded text-[11px] font-medium transition border ${
                    (bill.paymentMethod || 'on-account') === m.v
                      ? 'border-amber-700 bg-amber-700 text-white'
                      : 'border-amber-300 bg-white text-amber-900 hover:border-amber-500'
                  } ${isPushed ? 'opacity-60 cursor-not-allowed' : ''}`}
                  data-testid={`bd-payment-${m.v}`}
                >
                  {m.l}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-amber-700 mt-2">Pushed to Xero as a DRAFT supplier bill — the payment method is recorded on the Reference line so your bookkeeper sees it at a glance.</div>
          </div>
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-4 text-xs space-y-1">
          <div className="flex items-center gap-1.5 text-slate-700 font-medium"><Mail className="h-3.5 w-3.5" /> Source email</div>
          <div className="text-slate-600">From: <span className="font-mono">{bill.emailFrom}</span></div>
          <div className="text-slate-600">Date: {bill.emailDate}</div>
          {bill.attachmentNames?.length > 0 && (
            <div className="text-slate-600">Attachments: {bill.attachmentNames.join(', ')}</div>
          )}
          {bill.snippet && <div className="text-slate-500 italic mt-2">"{bill.snippet}"</div>}
        </div>
      )}

      {/* Supplier standard-discount prompt — when this supplier has a saved
          trade discount but the AI parser missed it on this bill. One-tap
          drops the discount in (rate-based) and routes by the supplier's
          configured apply mode. The Suppliers page is where the rate is
          configured — link below jumps straight there. */}
      {matchedSupplier
        && Number(matchedSupplier.defaultDiscountRate) > 0
        && !((bill.discount || 0) > 0)
        && !(bill.lineItems || []).some(li => Number(li.discountRate) > 0 || Number(li.discountAmount) > 0)
        && !isPushed && (
          <div
            className="bg-blue-50 border border-blue-300 rounded-md p-4 flex items-start gap-3"
            data-testid="supplier-discount-prompt"
          >
            <Percent className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-medium text-blue-900">
                {matchedSupplier.name} usually has a {(matchedSupplier.defaultDiscountRate * 100).toFixed(1)}% trade discount
              </div>
              <div className="text-xs text-blue-800 mt-0.5">
                The AI didn't find a discount on this bill. Apply the standard rate or
                <Link to={`/admin/suppliers/${matchedSupplier.id}`} className="underline ml-1">change it on the supplier page</Link>.
              </div>
            </div>
            <Button
              size="sm"
              onClick={applyStandardDiscount}
              data-testid="apply-supplier-discount-btn"
            >
              Apply {(matchedSupplier.defaultDiscountRate * 100).toFixed(1)}%
            </Button>
          </div>
        )}

      {/* Math reconciliation warning — shown only if the parser couldn't
          balance subtotal − discount + tax to total within 2p. We auto-fixed
          subtotal so Xero won't reject it, but the source numbers were
          inconsistent (likely OCR misread on a faded receipt) so admin should
          double-check before pushing. */}
      {Math.abs(Number(bill.mathDelta) || 0) > 0.02 && (
        <div
          className="bg-amber-50 border border-amber-300 rounded-md p-4 flex items-start gap-3"
          data-testid="math-delta-warning"
        >
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 space-y-1">
            <div className="font-medium">Receipt numbers don't balance — please verify</div>
            <div className="text-xs text-amber-800">
              The subtotal, discount, VAT and total on this receipt are
              off by <span className="font-mono font-medium">£{Math.abs(Number(bill.mathDelta)).toFixed(2)}</span>.
              We've adjusted the subtotal to make Subtotal − Discount + VAT = Total,
              but the OCR may have mis-read a number. Double-check the line items
              and totals below before pushing to Xero.
            </div>
          </div>
        </div>
      )}

      {/* Header fields */}
      <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
        <div className="font-display uppercase tracking-wide text-sm text-slate-700">Header</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SupplierAutocomplete
            value={bill.supplierName}
            onChange={v => updateField('supplierName', v)}
            suppliers={suppliers}
            disabled={isPushed}
            testId="field-supplier"
          />
          <Field label="Supplier email" value={bill.supplierEmail} onChange={v => updateField('supplierEmail', v)} disabled={isPushed} />
          <Field label="Invoice #" value={bill.invoiceNumber} onChange={v => updateField('invoiceNumber', v)} disabled={isPushed} testId="field-invoice-number" />
          <Field label="VAT no." value={bill.supplierVatNumber} onChange={v => updateField('supplierVatNumber', v)} disabled={isPushed} />
          <Field label="Issue date" type="date" value={bill.issueDate} onChange={v => updateField('issueDate', v)} disabled={isPushed} />
          <Field label="Due date" type="date" value={bill.dueDate} onChange={v => updateField('dueDate', v)} disabled={isPushed} />
        </div>
      </div>

      {/* Line items */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <div className="font-display uppercase tracking-wide text-sm text-slate-700">Line items</div>
            {inventoryLineCount > 0 && (
              <div className="text-xs text-blue-700 mt-1 flex items-center gap-1">
                <Boxes className="h-3 w-3" /> {inventoryLineCount} line(s) flagged as inventory (Cost of Sales) — they'll create draft parts on push.
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasAttachment && lines.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={runVerifyAll}
                disabled={verifyingAll || isPushed}
                data-testid="verify-all-btn"
              >
                {verifyingAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                {verifyingAll ? 'Verifying...' : 'Verify all'}
              </Button>
            )}
            {!isPushed && (
              <Button variant="outline" size="sm" onClick={addLine} data-testid="add-line-btn">
                <Plus className="h-4 w-4 mr-2" /> Add line
              </Button>
            )}
          </div>
        </div>
        {/* Desktop: full editable table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">SKU</th>
                <th className="text-left px-4 py-2 min-w-[260px]">Description</th>
                <th className="text-right px-4 py-2">Qty</th>
                <th className="text-right px-4 py-2">Unit £</th>
                <th className="text-right px-4 py-2">VAT %</th>
                <th className="text-left px-4 py-2 min-w-[280px]">Xero account</th>
                <th className="text-right px-4 py-2">Total</th>
                {!isPushed && <th className="px-2"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lines.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400 italic">No line items — add one above.</td></tr>
              ) : lines.map((li, i) => {
                const acct = accountByCode[li.accountCode];
                const isInv = isInventoryAccount(acct);
                const qty = Number(li.quantity) || 0;
                const unit = Number(li.unitAmount) || 0;
                const dRate = Number(li.discountRate) || 0;
                const dAmount = Number(li.discountAmount) || 0;
                const lineGross = qty * unit;
                const lineDiscount = dAmount > 0 ? dAmount : (lineGross * dRate);
                const lineTotal = Math.max(0, lineGross - lineDiscount);
                return (
                  <tr key={i} data-testid={`line-row-${i}`} className={isInv ? 'bg-blue-50/40' : ''}>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        <Input value={li.sku || ''} disabled={isPushed} className="h-8 text-xs font-mono"
                          onChange={e => updateLine(i, { sku: e.target.value })} placeholder="—" />
                        <VerifyButton
                          verification={li.verification}
                          loading={!!verifying[i]}
                          onClick={() => runVerify(i)}
                          disabled={isPushed || !hasAttachment}
                          testId={`verify-line-${i}`}
                        />
                      </div>
                    </td>
                    <td className="px-2 py-1.5">
                      <Input value={li.description || ''} disabled={isPushed} className="h-8"
                        onChange={e => updateLine(i, { description: e.target.value })} />
                    </td>
                    <td className="px-2 py-1.5 w-20">
                      <Input type="number" step="any" value={li.quantity ?? 1} disabled={isPushed}
                        className="h-8 text-right" onChange={e => updateLine(i, { quantity: parseFloat(e.target.value) || 0 })} />
                    </td>
                    <td className="px-2 py-1.5 w-24">
                      <Input type="number" step="0.01" value={li.unitAmount ?? 0} disabled={isPushed}
                        className="h-8 text-right" onChange={e => updateLine(i, { unitAmount: parseFloat(e.target.value) || 0 })} />
                    </td>
                    <td className="px-2 py-1.5 w-20">
                      <Input type="number" step="0.01" value={li.taxRate ?? 0} disabled={isPushed}
                        className="h-8 text-right" onChange={e => updateLine(i, { taxRate: parseFloat(e.target.value) || 0 })} />
                    </td>
                    <td className="px-2 py-1.5">
                      <AccountSelect
                        accounts={accounts}
                        value={li.accountCode || ''}
                        onChange={(code) => updateLine(i, { accountCode: code, suggestedAccountCode: '' })}
                        disabled={isPushed || !xeroConnected}
                        testId={`account-select-${i}`}
                      />
                      {li.verification && (
                        <VerifyNote verification={li.verification} />
                      )}
                      {li.suggestedAccountCode && li.suggestedAccountCode === li.accountCode && (
                        <div className="text-[10px] text-violet-700 mt-1 flex items-center gap-1" data-testid={`suggested-badge-${i}`}>
                          <Sparkles className="h-3 w-3" /> Auto-suggested — change if needed
                        </div>
                      )}
                      {isInv && (
                        <LineDestBadge
                          line={li}
                          categoryById={Object.fromEntries(categories.map(c => [c.id, c]))}
                          storeById={Object.fromEntries(stores.map(s => [s.id, s]))}
                          locationsByStore={locationsByStore}
                          woById={Object.fromEntries(workOrdersList.map(w => [w.id, w]))}
                          onEdit={() => setDestLineIdx(i)}
                          disabled={isPushed && !draftPartsByLine[(li.description || '').trim()]}
                          isPushed={isPushed}
                          draftPart={draftPartsByLine[(li.description || '').trim()]}
                          testId={`line-dest-${i}`}
                        />
                      )}
                    </td>
                    <td className="px-4 py-1.5 text-right font-medium tabular-nums">
                      {fmtMoney(lineTotal, bill.currency)}
                      {lineDiscount > 0 && (
                        <div className="text-[10px] text-amber-700 font-normal" title="Trade discount on this line">
                          {dRate > 0 ? `−${(dRate * 100).toFixed(1)}%` : `−${fmtMoney(dAmount, bill.currency)}`}
                        </div>
                      )}
                    </td>
                    {!isPushed && (
                      <td className="px-2 py-1.5 w-10">
                        <Button variant="ghost" size="sm" onClick={() => removeLine(i)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" title="Remove line">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-slate-50 text-sm">
              <tr><td colSpan={6} className="px-4 py-2 text-right text-slate-600">Subtotal</td>
                <td className="px-4 py-2 text-right font-medium">{fmtMoney(bill.subtotal, bill.currency)}</td><td /></tr>
              {(bill.discount || 0) > 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-2 text-right text-amber-700">Trade discount</td>
                  <td className="px-4 py-2 text-right font-medium text-amber-700 tabular-nums">−{fmtMoney(bill.discount, bill.currency)}</td>
                  <td />
                </tr>
              )}
              <tr><td colSpan={6} className="px-4 py-2 text-right text-slate-600">VAT</td>
                <td className="px-4 py-2 text-right font-medium">{fmtMoney(bill.tax, bill.currency)}</td><td /></tr>
              <tr><td colSpan={6} className="px-4 py-2 text-right text-slate-700 font-medium">Total</td>
                <td className="px-4 py-2 text-right font-display text-lg">{fmtMoney(bill.total, bill.currency)}</td><td /></tr>
            </tfoot>
          </table>
        </div>

        {/* Mobile: card-stacked editable lines */}
        <div className="lg:hidden divide-y divide-slate-100">
          {lines.length === 0 ? (
            <div className="px-4 py-12 text-center text-slate-400 italic">No line items — add one above.</div>
          ) : lines.map((li, i) => {
            const acct = accountByCode[li.accountCode];
            const isInv = isInventoryAccount(acct);
            const qty = Number(li.quantity) || 0;
            const unit = Number(li.unitAmount) || 0;
            const dRate = Number(li.discountRate) || 0;
            const dAmount = Number(li.discountAmount) || 0;
            const lineGross = qty * unit;
            const lineDiscount = dAmount > 0 ? dAmount : (lineGross * dRate);
            const lineTotal = Math.max(0, lineGross - lineDiscount);
            return (
              <div key={i} className={`p-4 space-y-3 ${isInv ? 'bg-blue-50/40' : ''}`} data-testid={`line-card-${i}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 text-xs uppercase tracking-wider text-slate-400">Line {i + 1}</div>
                  <VerifyButton
                    verification={li.verification}
                    loading={!!verifying[i]}
                    onClick={() => runVerify(i)}
                    disabled={isPushed || !hasAttachment}
                    testId={`verify-line-mobile-${i}`}
                    showLabel
                  />
                  {!isPushed && (
                    <Button variant="ghost" size="sm" onClick={() => removeLine(i)}
                      className="h-7 w-7 p-0 -mt-1 -mr-1 text-red-500 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Description</Label>
                  <Input value={li.description || ''} disabled={isPushed} className="mt-1"
                    onChange={e => updateLine(i, { description: e.target.value })} />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Qty</Label>
                    <Input type="number" step="any" value={li.quantity ?? 1} disabled={isPushed}
                      className="mt-1 text-right" onChange={e => updateLine(i, { quantity: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Unit £</Label>
                    <Input type="number" step="0.01" value={li.unitAmount ?? 0} disabled={isPushed}
                      className="mt-1 text-right" onChange={e => updateLine(i, { unitAmount: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">VAT</Label>
                    <Input type="number" step="0.01" value={li.taxRate ?? 0} disabled={isPushed}
                      className="mt-1 text-right" onChange={e => updateLine(i, { taxRate: parseFloat(e.target.value) || 0 })} />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">SKU</Label>
                  <Input value={li.sku || ''} disabled={isPushed} className="mt-1 font-mono text-xs"
                    onChange={e => updateLine(i, { sku: e.target.value })} placeholder="—" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Xero account</Label>
                  <div className="mt-1">
                    <AccountSelect
                      accounts={accounts}
                      value={li.accountCode || ''}
                      onChange={(code) => updateLine(i, { accountCode: code, suggestedAccountCode: '' })}
                      disabled={isPushed || !xeroConnected}
                      testId={`account-select-mobile-${i}`}
                    />
                  </div>
                  {li.verification && (
                    <VerifyNote verification={li.verification} />
                  )}
                  {li.suggestedAccountCode && li.suggestedAccountCode === li.accountCode && (
                    <div className="text-[10px] text-violet-700 mt-1.5 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" /> Auto-suggested — change if needed
                    </div>
                  )}
                  {isInv && (
                    <div className="mt-1.5">
                      <LineDestBadge
                        line={li}
                        categoryById={Object.fromEntries(categories.map(c => [c.id, c]))}
                        storeById={Object.fromEntries(stores.map(s => [s.id, s]))}
                        locationsByStore={locationsByStore}
                        woById={Object.fromEntries(workOrdersList.map(w => [w.id, w]))}
                        onEdit={() => setDestLineIdx(i)}
                        disabled={isPushed && !draftPartsByLine[(li.description || '').trim()]}
                        isPushed={isPushed}
                        draftPart={draftPartsByLine[(li.description || '').trim()]}
                        testId={`line-dest-mobile-${i}`}
                      />
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                  <span className="text-xs text-slate-500">
                    Line total
                    {lineDiscount > 0 && (
                      <span className="ml-2 text-amber-700 text-[11px] font-medium" data-testid={`line-discount-${i}`}>
                        {dRate > 0 ? `(−${(dRate * 100).toFixed(1)}%)` : `(−${fmtMoney(dAmount, bill.currency)})`}
                      </span>
                    )}
                  </span>
                  <span className="font-display text-base tabular-nums">{fmtMoney(lineTotal, bill.currency)}</span>
                </div>
              </div>
            );
          })}
          {/* Mobile totals */}
          {lines.length > 0 && (
            <div className="bg-slate-50 p-4 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-medium tabular-nums">{fmtMoney(bill.subtotal, bill.currency)}</span></div>
              {(bill.discount || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-amber-700">Trade discount</span>
                  <span className="font-medium text-amber-700 tabular-nums">−{fmtMoney(bill.discount, bill.currency)}</span>
                </div>
              )}
              <div className="flex justify-between"><span className="text-slate-600">VAT</span><span className="font-medium tabular-nums">{fmtMoney(bill.tax, bill.currency)}</span></div>
              <div className="flex justify-between pt-1.5 border-t border-slate-200">
                <span className="text-slate-700 font-medium">Total</span>
                <span className="font-display text-lg tabular-nums">{fmtMoney(bill.total, bill.currency)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
        </div>  {/* /right column */}
      </div>  {/* /split grid */}

      {/* Mobile/tablet: full-screen invoice preview dialog */}
      <Dialog open={pdfMobileOpen} onOpenChange={setPdfMobileOpen}>
        <DialogContent className="max-w-3xl w-full p-0 gap-0 h-[92vh] sm:h-[85vh] flex flex-col" data-testid="invoice-mobile-dialog">
          <DialogHeader className="px-4 py-3 border-b border-slate-200 flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-sm font-display uppercase tracking-wide flex items-center gap-2">
              <FileText className="h-4 w-4" /> Original invoice
            </DialogTitle>
            {pdfUrl && (
              <a
                href={pdfUrl}
                download={(bill.attachmentNames || [])[0] || 'invoice.pdf'}
                className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 mr-6"
              >
                <Download className="h-3 w-3" /> Download
              </a>
            )}
          </DialogHeader>
          <div className="flex-1 min-h-0">
            <PdfViewer pdfUrl={pdfUrl} pdfLoading={pdfLoading} pdfError={pdfError} pdfIsImage={pdfIsImage} heightClass="h-full" />
          </div>
        </DialogContent>
      </Dialog>

      {/* Inventory destination picker — appears when the admin picks a
          COGS account on a bill line. Lets them decide upfront whether
          the part goes into stock or straight onto a work order.
          On PUSHED bills, we go straight to `approveDraftPart` so the
          admin can retroactively approve a draft that landed in Draft
          Inventory before this UX existed. */}
      <LineDestinationDialog
        open={destLineIdx !== null}
        line={destLineIdx !== null ? (bill.lineItems || [])[destLineIdx] : null}
        isPushed={isPushed}
        onClose={() => setDestLineIdx(null)}
        onSave={async (dest) => {
          if (destLineIdx === null) return;
          const li = (bill.lineItems || [])[destLineIdx];
          const draftPart = draftPartsByLine[(li?.description || '').trim()];
          if (isPushed && draftPart) {
            // Retro-approval path — call the approve endpoint straight
            // away so the part actually gets stocked / attached now.
            try {
              // First patch the draft with the chosen category / sell
              // price so the approve endpoint has everything it needs.
              await updateDraftPart(draftPart.id, {
                categoryId: dest.categoryId,
              });
              await approveDraftPart(draftPart.id, {
                mode: dest.mode,
                storeId: dest.storeId || null,
                locationId: dest.locationId || null,
                workOrderId: dest.workOrderId || null,
                alreadyBilled: !!dest.alreadyBilled,
              });
              toast.success(dest.mode === 'stock'
                ? 'Stocked from this bill line.'
                : 'Attached to the work order.');
              await refreshBillDraftParts();
              setDestLineIdx(null);
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Approval failed');
            }
          } else {
            // Pre-push path — persist on the bill line, the push handler
            // will approve during Xero submission.
            updateLine(destLineIdx, { inventoryDestination: dest });
            setDestLineIdx(null);
          }
        }}
        onClear={() => {
          if (destLineIdx !== null) updateLine(destLineIdx, { inventoryDestination: null });
          setDestLineIdx(null);
        }}
        categories={categories}
        stores={stores}
        locationsByStore={locationsByStore}
        workOrders={workOrdersList}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Line destination badge — inline summary of the chosen inventory
// destination for a COGS line, or a "Set destination" CTA when nothing
// has been picked yet. Both variants open the picker dialog.
// ---------------------------------------------------------------------------
const LineDestBadge = ({ line, categoryById, storeById, locationsByStore, woById, onEdit, disabled, isPushed, draftPart, testId }) => {
  const dest = line?.inventoryDestination;
  // ---- POST-PUSH RENDERING ----
  // If the bill has been pushed, prefer the LIVE draft-part state
  // over the frozen bill-line snapshot: the destination stamp on
  // `line.inventoryDestination` from push-time might be stale (part
  // could have already been approved elsewhere). Two cases:
  //   1. Draft still exists → let the admin set the destination now.
  //   2. Draft is gone → part has been approved. Show a subtle
  //      "Approved" pill so the admin knows there's nothing to do.
  if (isPushed) {
    if (!draftPart) {
      return (
        <div className="text-[10px] text-emerald-800 mt-1 flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
          <CheckCircle2 className="h-3 w-3" /> Approved (stock or attached)
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={disabled ? undefined : onEdit}
        disabled={disabled}
        data-testid={`${testId}-set`}
        className="text-[10px] text-blue-700 mt-1 flex items-center gap-1 hover:underline disabled:no-underline disabled:opacity-70"
      >
        <Boxes className="h-3 w-3" /> Waiting in Draft Inventory — <span className="underline">set destination now</span>
      </button>
    );
  }
  // ---- PRE-PUSH RENDERING (unchanged) ----
  if (!dest || !dest.mode) {
    return (
      <button
        type="button"
        onClick={disabled ? undefined : onEdit}
        disabled={disabled}
        data-testid={`${testId}-set`}
        className="text-[10px] text-blue-700 mt-1 flex items-center gap-1 hover:underline disabled:no-underline disabled:opacity-70"
      >
        <Boxes className="h-3 w-3" /> Will create draft inventory item — <span className="underline">set destination</span>
      </button>
    );
  }
  const cat = categoryById[dest.categoryId];
  if (dest.mode === 'stock') {
    const store = storeById[dest.storeId];
    const locs = locationsByStore[dest.storeId] || [];
    const loc = locs.find(l => l.id === dest.locationId);
    return (
      <button
        type="button"
        onClick={disabled ? undefined : onEdit}
        disabled={disabled}
        data-testid={`${testId}-edit`}
        className="text-[10px] text-emerald-800 mt-1 flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 hover:bg-emerald-100 disabled:opacity-70"
      >
        <Package className="h-3 w-3" />
        <span className="truncate">Stock → {store?.name || dest.storeId} / {loc?.name || dest.locationId}</span>
        {cat && <span className="text-emerald-700">· {cat.name} ({cat.markupPct}%)</span>}
      </button>
    );
  }
  // attach-wo
  const wo = woById[dest.workOrderId];
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onEdit}
      disabled={disabled}
      data-testid={`${testId}-edit`}
      className="text-[10px] text-blue-800 mt-1 flex items-start gap-1 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 hover:bg-blue-100 disabled:opacity-70"
    >
      <Wrench className="h-3 w-3 mt-0.5" />
      <span className="truncate text-left">
        Attach → <span className="font-mono">{dest.workOrderId}</span>
        {wo?.title && <span className="text-blue-700"> · {wo.title}</span>}
        {dest.alreadyBilled
          ? <span className="text-slate-600"> · already billed</span>
          : <span className="text-emerald-700"> · will bill</span>}
        {cat && <span className="text-blue-700"> · {cat.name} ({cat.markupPct}%)</span>}
      </span>
    </button>
  );
};

// ---------------------------------------------------------------------------
// Destination dialog — the shared UI the admin sees when picking Stock
// vs Attach-to-WO for a COGS bill line. Mirrors the DraftInventory
// approval dialog but is embedded inline in bill approval.
// ---------------------------------------------------------------------------
const LineDestinationDialog = ({ open, line, isPushed, onClose, onSave, onClear, categories, stores, locationsByStore, workOrders }) => {
  const existing = line?.inventoryDestination || {};
  const [mode, setMode] = useState(existing.mode || 'stock');
  const [categoryId, setCategoryId] = useState(existing.categoryId || '');
  const [storeId, setStoreId] = useState(existing.storeId || '');
  const [locationId, setLocationId] = useState(existing.locationId || '');
  const [workOrderId, setWorkOrderId] = useState(existing.workOrderId || '');
  const [alreadyBilled, setAlreadyBilled] = useState(!!existing.alreadyBilled);
  const [woQuery, setWoQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    const cur = line?.inventoryDestination || {};
    setMode(cur.mode || 'stock');
    setCategoryId(cur.categoryId || '');
    setStoreId(cur.storeId || (stores[0]?.id || ''));
    setLocationId(cur.locationId || (locationsByStore[cur.storeId || stores[0]?.id] || [])[0]?.id || '');
    setWorkOrderId(cur.workOrderId || '');
    setAlreadyBilled(!!cur.alreadyBilled);
    setWoQuery('');
  }, [open, line]);

  const filteredWos = useMemo(() => {
    const custName = (w) => w.customerName || w.customer || '';
    const q = (woQuery || '').trim().toLowerCase();
    const all = (workOrders || []).slice().sort((a, b) => {
      const ac = a.status === 'completed' ? 1 : 0;
      const bc = b.status === 'completed' ? 1 : 0;
      if (ac !== bc) return ac - bc;
      return (b.id || '').localeCompare(a.id || '');
    });
    if (!q) return all.slice(0, 60);
    return all.filter(w =>
      (w.id || '').toLowerCase().includes(q)
      || (w.title || '').toLowerCase().includes(q)
      || custName(w).toLowerCase().includes(q)
      || (w.siteName || '').toLowerCase().includes(q),
    ).slice(0, 60);
  }, [workOrders, woQuery]);

  if (!open || !line) return null;
  const cat = categories.find(c => c.id === categoryId);
  const unitCost = Number(line.unitAmount || 0);
  const previewSell = cat ? Math.round(unitCost * (1 + (Number(cat.markupPct) || 0) / 100) * 100) / 100 : null;
  const qty = Math.round(Number(line.quantity) || 1);
  const locs = locationsByStore[storeId] || [];
  const canSave = !!categoryId && (
    (mode === 'stock' && !!storeId && !!locationId)
    || (mode === 'attach-wo' && !!workOrderId)
  );

  const handleSave = () => {
    const dest = { mode, categoryId };
    if (mode === 'stock') { dest.storeId = storeId; dest.locationId = locationId; }
    if (mode === 'attach-wo') { dest.workOrderId = workOrderId; dest.alreadyBilled = !!alreadyBilled; }
    onSave(dest);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        data-testid="line-destination-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl pr-6">
            {isPushed ? 'Approve this bill line now' : 'Where should this go?'}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            {line.description || 'This bill line'} — {qty} × £{unitCost.toFixed(2)}.
            {isPushed
              ? ' This bill has already been pushed to Xero. Choose a destination and we\u2019ll approve the draft part now.'
              : ' Pick a destination now so we skip the Draft Inventory step on push.'}
          </DialogDescription>
        </DialogHeader>

        {/* Mode selector */}
        <div className="grid grid-cols-2 gap-2 pt-2">
          <button
            type="button"
            onClick={() => setMode('stock')}
            data-testid="dest-mode-stock"
            className={`text-left rounded-md border-2 p-3 transition-colors ${mode === 'stock' ? 'border-green-600 bg-green-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <Boxes className={`h-4 w-4 ${mode === 'stock' ? 'text-green-700' : 'text-slate-500'}`} />
              <span className="text-sm font-semibold">Stock it</span>
            </div>
            <div className="text-[11px] text-slate-600 leading-snug">
              Add {qty} unit{qty === 1 ? '' : 's'} into inventory at a store/location.
            </div>
          </button>
          <button
            type="button"
            onClick={() => setMode('attach-wo')}
            data-testid="dest-mode-attach-wo"
            className={`text-left rounded-md border-2 p-3 transition-colors ${mode === 'attach-wo' ? 'border-blue-600 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <Wrench className={`h-4 w-4 ${mode === 'attach-wo' ? 'text-blue-700' : 'text-slate-500'}`} />
              <span className="text-sm font-semibold">Attach to work order</span>
            </div>
            <div className="text-[11px] text-slate-600 leading-snug">
              Skip stock — bill straight through a specific job.
            </div>
          </button>
        </div>

        {/* Common: category (needed in BOTH modes for markup) */}
        <div className="pt-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500">
            Category <span className="text-red-500">*</span>
          </Label>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger className="mt-1" data-testid="dest-category-select">
              <SelectValue placeholder={categories.length ? 'Pick a category…' : 'No categories yet'} />
            </SelectTrigger>
            <SelectContent>
              {categories.map(c => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name} <span className="text-slate-400 text-xs ml-1">({c.markupPct}% markup)</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {cat && previewSell !== null && (
            <div className="text-[11px] text-slate-500 mt-1">
              Sell price will be <span className="font-medium text-slate-700">£{previewSell.toFixed(2)}</span> (cost £{unitCost.toFixed(2)} + {cat.markupPct}%).
            </div>
          )}
        </div>

        {/* Mode-specific fields */}
        {mode === 'stock' ? (
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Store</Label>
              <Select value={storeId} onValueChange={(v) => { setStoreId(v); setLocationId((locationsByStore[v] || [])[0]?.id || ''); }}>
                <SelectTrigger className="mt-1" data-testid="dest-store-select">
                  <SelectValue placeholder="Pick a store…" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Location</Label>
              <Select value={locationId} onValueChange={setLocationId} disabled={!storeId || locs.length === 0}>
                <SelectTrigger className="mt-1" data-testid="dest-location-select">
                  <SelectValue placeholder={locs.length ? 'Pick a location…' : 'No locations in this store'} />
                </SelectTrigger>
                <SelectContent>
                  {locs.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Work order</Label>
              <Input
                placeholder="Search by ID, title, customer or site…"
                value={woQuery}
                onChange={(e) => setWoQuery(e.target.value)}
                className="mt-1 h-9"
                data-testid="dest-wo-search"
              />
              <div className="mt-2 border border-slate-200 rounded-md divide-y divide-slate-100 max-h-56 overflow-y-auto">
                {filteredWos.length === 0 ? (
                  <div className="p-3 text-xs text-slate-500 text-center">No matching work orders.</div>
                ) : filteredWos.map(w => {
                  const active = workOrderId === w.id;
                  const status = (w.status || 'open').toLowerCase();
                  const isDone = status === 'completed';
                  const isUnsched = status === 'unscheduled';
                  const isCancelled = status === 'cancelled';
                  const custName = w.customerName || w.customer || '';
                  const statusLabel = status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  return (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => setWorkOrderId(w.id)}
                      data-testid={`dest-wo-option-${w.id}`}
                      className={`w-full text-left p-2.5 transition-colors ${active ? 'bg-blue-50 border-l-4 border-blue-600' : 'hover:bg-slate-50'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] font-mono text-slate-500">{w.id}</div>
                          <div className="text-sm font-medium truncate">{w.title || 'Untitled'}</div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {custName || 'No customer'}
                            {w.siteName ? ` · ${w.siteName}` : ''}
                          </div>
                        </div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${
                          isDone
                            ? 'bg-slate-100 text-slate-600'
                            : isCancelled
                              ? 'bg-red-100 text-red-700'
                              : isUnsched
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-emerald-100 text-emerald-800'
                        }`}>
                          {statusLabel}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Bill this / already billed */}
            <label
              className="flex items-start gap-2 rounded-md border border-slate-200 bg-white p-2.5 cursor-pointer hover:bg-slate-50"
              data-testid="dest-already-billed-row"
            >
              <Checkbox
                checked={alreadyBilled}
                onCheckedChange={(v) => setAlreadyBilled(!!v)}
                className="mt-0.5"
                data-testid="dest-already-billed-checkbox"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">Already billed to the customer</div>
                <div className="text-[11px] text-slate-600 mt-0.5">
                  {alreadyBilled
                    ? 'Do nothing on the invoice — record on the work order for tracking only.'
                    : 'Add value + markup to the customer\'s next draft invoice.'}
                </div>
              </div>
            </label>
          </div>
        )}

        <DialogFooter className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:justify-between">
          {existing.mode && !isPushed ? (
            <Button
              variant="ghost"
              onClick={onClear}
              className="text-red-600 hover:bg-red-50"
              data-testid="dest-clear-btn"
            >
              Clear destination
            </Button>
          ) : <span />}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:items-center">
            <Button variant="outline" onClick={onClose}>{isPushed ? 'Not now' : 'Decide later'}</Button>
            <Button
              onClick={handleSave}
              disabled={!canSave}
              data-testid="dest-save-btn"
              className={mode === 'stock' ? 'bg-green-700 hover:bg-green-800 text-white' : 'bg-blue-700 hover:bg-blue-800 text-white'}
            >
              <CheckCircle2 className="h-4 w-4 mr-1.5" />
              {isPushed ? 'Approve now' : 'Save destination'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PdfViewer = ({ pdfUrl, pdfLoading, pdfError, pdfIsImage = false, heightClass = 'h-[78vh]' }) => {
  if (pdfLoading) {
    return <div className={`${heightClass} flex items-center justify-center text-sm text-slate-500`}>Loading invoice…</div>;
  }
  if (pdfError) {
    return (
      <div className={`${heightClass} flex flex-col items-center justify-center gap-2 text-center px-6`}>
        <FileWarning className="h-8 w-8 text-amber-500" />
        <div className="text-sm text-slate-700 font-medium">Couldn't load original</div>
        <div className="text-xs text-slate-500 max-w-xs">{pdfError}</div>
      </div>
    );
  }
  if (!pdfUrl) {
    return <div className={`${heightClass} flex items-center justify-center text-sm text-slate-400 italic`}>No attachment available</div>;
  }
  // Receipts come in as images (JPEG/PNG/HEIC) — render with <img> in a
  // scrollable container so the user can zoom by ctrl+scroll on desktop or
  // pinch on mobile. Tap-to-open-in-new-tab gives full screen on phones.
  if (pdfIsImage) {
    return (
      <a
        href={pdfUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`${heightClass} block w-full bg-slate-100 overflow-auto p-2`}
        data-testid="invoice-pdf-iframe"
        title="Tap to open full-size"
      >
        <img
          src={pdfUrl}
          alt="Original receipt"
          className="w-full h-auto"
          loading="lazy"
        />
      </a>
    );
  }
  // <object> renders the PDF inline more reliably than <iframe> across
  // Chrome/Edge/Safari (especially when the source is a blob URL behind a
  // proxy). The fallback inside <object> shows when the browser has no PDF
  // viewer at all — typically headless or extension-blocked Chrome — so the
  // admin can still grab the file with one click.
  return (
    <object
      data={pdfUrl}
      type="application/pdf"
      className={`${heightClass} w-full bg-slate-100`}
      data-testid="invoice-pdf-iframe"
      aria-label="Original invoice"
    >
      <div className={`${heightClass} flex flex-col items-center justify-center gap-3 text-sm text-slate-500 p-6`}>
        <div>Your browser couldn't render the PDF inline.</div>
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-1.5 rounded bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium"
        >
          Open invoice in new tab
        </a>
      </div>
    </object>
  );
};

const Field = ({ label, value, onChange, type = 'text', disabled, testId }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    <Input className="mt-1" type={type} value={value || ''}
      disabled={disabled} onChange={e => onChange(e.target.value)} data-testid={testId} />
  </div>
);

/**
 * Supplier field with open-combobox behaviour: the admin can keep typing
 * freely, and as long as the typed value matches part of a known supplier
 * name the matching suggestions show up in a dropdown. Clicking a
 * suggestion commits that supplier's exact saved name; pressing Enter
 * (or tabbing out, or clicking outside) commits whatever is currently
 * typed. Works well for the bill-review screen where parsed names come
 * in with slight variations (e.g. "RS Components Ltd" vs "RS Components").
 */
const SupplierAutocomplete = ({ value, onChange, suppliers, disabled, testId }) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);

  const typed = (value || '').trim().toLowerCase();
  const matches = useMemo(() => {
    if (!typed) return [];
    // Match suppliers whose name contains the typed substring, but skip
    // an identical match (no point suggesting what's already typed).
    return (suppliers || [])
      .filter(s => {
        const n = (s.name || '').toLowerCase();
        return n && n !== typed && n.includes(typed);
      })
      .sort((a, b) => {
        // Prefer names that start with the typed text; break ties by
        // alphabetical order.
        const ap = (a.name || '').toLowerCase().startsWith(typed) ? 0 : 1;
        const bp = (b.name || '').toLowerCase().startsWith(typed) ? 0 : 1;
        if (ap !== bp) return ap - bp;
        return (a.name || '').localeCompare(b.name || '');
      })
      .slice(0, 8);
  }, [typed, suppliers]);

  useEffect(() => {
    // Close on outside click.
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const commit = (name) => {
    onChange(name);
    setOpen(false);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!open || matches.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        setOpen(false);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(matches.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(-1, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight >= 0) {
        commit(matches[highlight].name);
      } else {
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Label className="text-xs uppercase tracking-wider text-slate-500">Supplier</Label>
      <Input
        className="mt-1"
        value={value || ''}
        disabled={disabled}
        placeholder="Start typing to search suppliers..."
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(-1);
        }}
        onFocus={() => { if (!disabled) setOpen(true); }}
        onKeyDown={onKeyDown}
        data-testid={testId}
      />
      {open && matches.length > 0 && !disabled && (
        <div
          className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-md max-h-64 overflow-y-auto"
          data-testid="supplier-suggestions"
        >
          {matches.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onMouseDown={(e) => e.preventDefault() /* keep focus on input */}
              onClick={() => commit(s.name)}
              onMouseEnter={() => setHighlight(i)}
              className={`block w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-b-0 ${
                i === highlight ? 'bg-blue-50 text-blue-900' : 'hover:bg-slate-50 text-slate-900'
              }`}
              data-testid={`supplier-suggestion-${i}`}
            >
              <div className="font-medium truncate">{s.name}</div>
              {(s.defaultDiscountRate > 0 || s.email) && (
                <div className="text-xs text-slate-500 truncate">
                  {s.defaultDiscountRate > 0 && (
                    <>Trade {(s.defaultDiscountRate * 100).toFixed(1)}%</>
                  )}
                  {s.defaultDiscountRate > 0 && s.email && ' · '}
                  {s.email}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AccountSelect = ({ accounts, value, onChange, disabled, testId }) => {
  const [open, setOpen] = useState(false);

  // Group by type for readability — DIRECTCOSTS first. Ordering kept so
  // Cost-of-Sales accounts (which also create inventory parts) always
  // surface before Expenses / Overheads.
  const groups = useMemo(() => {
    const order = ['DIRECTCOSTS', 'EXPENSE', 'OVERHEADS', 'CURRLIAB', 'FIXED', 'OTHER'];
    const buckets = {};
    accounts.forEach(a => { (buckets[a.type] ||= []).push(a); });
    const ordered = [];
    order.forEach(t => { if (buckets[t]) { ordered.push([t, buckets[t]]); delete buckets[t]; } });
    Object.entries(buckets).forEach(([t, arr]) => ordered.push([t, arr]));
    return ordered;
  }, [accounts]);

  const labelFor = (t) => ({
    DIRECTCOSTS: 'Cost of Sales (creates inventory)',
    EXPENSE: 'Expenses',
    OVERHEADS: 'Overheads',
    CURRLIAB: 'Current Liabilities',
    FIXED: 'Fixed Assets',
  }[t] || t);

  const selected = useMemo(
    () => accounts.find(a => a.code === value) || null,
    [accounts, value]
  );

  if (!accounts.length) {
    // Preserve the "connect Xero" hint when no accounts are loaded.
    return (
      <button
        type="button"
        disabled
        data-testid={testId}
        className="h-8 w-full text-sm text-left px-3 border border-slate-200 rounded-md text-slate-400 bg-slate-50 cursor-not-allowed"
      >
        Connect Xero to load accounts
      </button>
    );
  }

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={`h-8 w-full text-sm text-left px-3 border rounded-md flex items-center gap-2 truncate ${
            disabled
              ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed'
              : 'bg-white border-slate-200 hover:border-slate-300 text-slate-900'
          }`}
        >
          {selected ? (
            <>
              <span className="font-mono text-xs text-slate-500 shrink-0">{selected.code}</span>
              <span className="truncate">{selected.name}</span>
            </>
          ) : (
            <span className="text-slate-400">Pick an account…</span>
          )}
          <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400 ml-auto shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[360px]" align="start">
        <Command
          // cstom filter: search across both the account *code* (number) and
          // *name* — so typing "4000" matches "Sales" (code 4000) and typing
          // "sales" also matches it.
          filter={(cmdValue, search) => {
            if (!search) return 1;
            const needle = search.toLowerCase();
            return cmdValue.toLowerCase().includes(needle) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search by code or name…" data-testid={`${testId}-search`} />
          <CommandList className="max-h-80">
            <CommandEmpty>No matching accounts</CommandEmpty>
            {groups.map(([type, arr]) => (
              <CommandGroup key={type} heading={labelFor(type)}>
                {arr.map(a => (
                  <CommandItem
                    key={a.code}
                    // cmdk uses `value` for filter matching — include both
                    // code and name so the search covers both.
                    value={`${a.code} ${a.name}`}
                    onSelect={() => {
                      onChange(a.code);
                      setOpen(false);
                    }}
                    data-testid={`account-option-${a.code}`}
                    className="gap-2"
                  >
                    <span className="font-mono text-xs text-slate-500 w-12 shrink-0">{a.code}</span>
                    <span className="truncate">{a.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const VerifyButton = ({ verification, loading, onClick, disabled, testId, showLabel = false }) => {
  // Default (un-verified) icon style
  let Icon = ShieldCheck;
  let cls = 'text-slate-400 hover:text-slate-700';
  let title = 'Cross-check this line against the original PDF';
  if (verification?.status === 'match') {
    Icon = ShieldCheck;
    cls = 'text-green-700 hover:text-green-800 hover:bg-green-50';
    title = `Verified against invoice — ${verification.notes || 'looks correct'}`;
  } else if (verification?.status === 'partial') {
    Icon = ShieldAlert;
    cls = 'text-amber-600 hover:text-amber-700 hover:bg-amber-50';
    title = `Partial match — ${verification.notes || 'some fields differ'}`;
  } else if (verification?.status === 'mismatch') {
    Icon = ShieldX;
    cls = 'text-red-600 hover:text-red-700 hover:bg-red-50';
    title = `Mismatch — ${verification.notes || 'cannot find this line on the invoice'}`;
  }
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      disabled={disabled || loading}
      title={title}
      data-testid={testId}
      className={`shrink-0 ${showLabel ? 'h-7 px-2 text-[11px] gap-1' : 'h-8 w-8 p-0'} ${cls}`}
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
      {showLabel && <span>{loading ? 'Verifying' : verification ? 'Re-verify' : 'AI verify'}</span>}
    </Button>
  );
};

const VerifyNote = ({ verification }) => {
  const styles = {
    match:    { cls: 'text-green-700', Icon: ShieldCheck, label: 'Verified' },
    partial:  { cls: 'text-amber-700', Icon: ShieldAlert, label: 'Partial match' },
    mismatch: { cls: 'text-red-700',   Icon: ShieldX,     label: 'Mismatch' },
  };
  const cfg = styles[verification.status] || styles.partial;
  const C = cfg.Icon;
  return (
    <div className={`text-[10px] mt-1 flex items-start gap-1 ${cfg.cls}`} data-testid={`verify-note-${verification.status}`}>
      <C className="h-3 w-3 mt-0.5 shrink-0" />
      <div className="leading-tight">
        <span className="font-medium">{cfg.label}.</span>
        {verification.notes ? <> {verification.notes}</> : null}
        {(verification.issues || []).length > 0 && (
          <ul className="list-disc ml-4 mt-0.5">
            {verification.issues.slice(0, 3).map((it, k) => (
              <li key={k}>
                <span className="font-medium">{it.field}:</span> expected <code className="font-mono">{String(it.expected)}</code>, found <code className="font-mono">{String(it.found)}</code>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default BillDetail;
